import { Position } from 'reactflow';
import { HandleConfig } from '../types';

/**
 * Calculates the position style for handles based on their index and position
 * 
 * @param index The index of the handle in the array of handles for this position
 * @param total The total number of handles for this position
 * @param handlePosition The position of the handle (top, bottom, left, right)
 * @returns CSS properties object for positioning the handle
 */
export const calculateHandleStyle = (index: number, total: number, handlePosition: Position): React.CSSProperties => {
  const calcOffset = 5;
  const percentage = (index + 1) * (100 / (total + 1));

  switch (handlePosition) {
    case Position.Top:
      return { left: `calc(${percentage}% - ${calcOffset}px)`, top: `-${calcOffset}px` };
    case Position.Bottom:
      return { left: `calc(${percentage}% - ${calcOffset}px)`, bottom: `-${calcOffset}px` };
    case Position.Left:
    case Position.Right:
    default:
      return { top: `calc(${percentage}% - ${calcOffset}px)` };
  }
};

/**
 * Groups handles by their position (top, bottom, left, right)
 * 
 * @param handles Array of handle configurations
 * @returns Object with handles grouped by position
 */
export const groupHandlesByPosition = (handles: HandleConfig[]): Record<Position, HandleConfig[]> => {
  return handles.reduce((acc, handle) => {
    if (!acc[handle.position]) {
      acc[handle.position] = [];
    }
    acc[handle.position].push(handle);
    return acc;
  }, {} as Record<Position, HandleConfig[]>);
};

/**
 * Constants for node dimensions and thresholds
 */
export const NODE_DIMENSIONS = {
  MIN_WIDTH: 56,
  MAX_WIDTH: 400,
  MIN_HEIGHT: 48,
  MAX_HEIGHT: 180,
  
  // Content visibility thresholds
  HIDE_LABELS_WIDTH_THRESHOLD: 150,
  HIDE_NOTES_HEIGHT_THRESHOLD: 70,
  
  // Content height estimations
  HEADER_CONTENT_ESTIMATED_HEIGHT: 32,
  LABEL_ROW_ESTIMATED_HEIGHT: 22,
  NOTES_MIN_VISIBLE_HEIGHT_ADDITION: 25,
  MODULES_MIN_VISIBLE_HEIGHT_ADDITION: 25, // Added for ClusterNode modules
  AVATAR_STACK_MIN_VISIBLE_HEIGHT_ADDITION: 28, // Added for SyncInjectorNode avatar stack
  VERTICAL_PADDING: 8
};

/**
 * Returns the default width for a given node type.
 * @param nodeType The type of the node.
 * @returns The default width in pixels.
 */
export const getDefaultNodeWidth = (nodeType: string): number => {
  switch (nodeType) {
    case 'cluster':
      return 216;
    case 'destination':
      return 200;
    default:
      return 160;
  }
};
